import { useEffect, useMemo, useRef, useState } from 'react';
import './NhisTrendChart.css';

const DOMAIN_ORDER = [
  '비만',
  '고혈압',
  '당뇨병',
  '신장질환',
  '만성신장질환',
  '간장질환',
  '빈혈',
  '시각',
  '청각',
  '폐결핵흉부질환',
];

const STATUS_PRIORITY = {
  suspected: 4,
  chronic: 4,
  borderline: 3,
  normal: 2,
  unknown: 1,
  no_data: 1,
  not_applicable: 1,
};

function fmtDate(s) {
  const str = String(s ?? '');
  if (str.length === 8) return `${str.slice(0, 4)}.${str.slice(4, 6)}.${str.slice(6, 8)}`;
  if (str.length === 10 && str.includes('-')) return str.replaceAll('-', '.');
  return str;
}

function fmtAxisDate(s) {
  const str = String(s ?? '');
  if (str.length === 8) return `${str.slice(2, 4)}.${str.slice(4, 6)}`;
  return str.slice(0, 5);
}

function normalizeKey(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[()[\]{}.,/·_-]/g, '')
    .replace(/\s+/g, '')
    .trim();
}

function normalizeGenderValue(value) {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return '';
  if (raw === 'male' || raw === 'm' || raw === 'man' || raw === '남' || raw === '남성') return 'male';
  if (raw === 'female' || raw === 'f' || raw === 'woman' || raw === '여' || raw === '여성') return 'female';
  return raw;
}

function classifyDomainByIndicator(indicator) {
  const key = normalizeKey(indicator);
  if (!key) return '기타';
  if (
    key.includes('체질량지수')
    || key.includes('bmi')
    || key.includes('허리둘레')
    || key.includes('체중')
    || key.includes('신장')
  ) return '비만';
  if (key.includes('혈압')) return '고혈압';
  if (key.includes('공복혈당')) return '당뇨병';
  if (key.includes('요단백')) return '신장질환';
  if (key.includes('혈청크레아티닌') || key.includes('신사구체여과율') || key.includes('gfr')) return '만성신장질환';
  if (
    key.includes('에이에스티')
    || key.includes('ast')
    || key.includes('에이엘티')
    || key.includes('alt')
    || key.includes('감마지티피')
    || key.includes('gtp')
  ) return '간장질환';
  if (key.includes('혈색소')) return '빈혈';
  if (key.includes('시력')) return '시각';
  if (key.includes('청각')) return '청각';
  if (key.includes('폐결핵')) return '폐결핵흉부질환';
  return '기타';
}

function toNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const parsed = Number(String(value ?? '').trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function formatNumber(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  if (Number.isInteger(value)) return String(value);
  const rounded = Math.round(value * 10) / 10;
  return String(rounded);
}

function formatIndicatorLabel(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '-';
  if (raw === '체질량지수') return 'BMI';
  if (raw === '혈압(수축기)' || raw === '수축기 혈압') return '최고(수축기)';
  if (raw === '혈압(이완기)' || raw === '이완기 혈압') return '최저(이완기)';
  if (raw === '허리둘레') return '허리둘레';
  if (raw === '체중') return '체중';
  if (raw === '신장') return '신장';
  return raw;
}

function normalizeValues(values) {
  if (!Array.isArray(values)) return [];
  return values
    .map((point) => ({
      date: String(point?.date ?? ''),
      value: toNumber(point?.value),
    }))
    .filter((point) => point.value !== null)
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

function buildStatusMap(healthCheckupOverview) {
  const common = Array.isArray(healthCheckupOverview?.commonItems) ? healthCheckupOverview.commonItems : [];
  const optional = Array.isArray(healthCheckupOverview?.optionalItems) ? healthCheckupOverview.optionalItems : [];
  const allItems = [...common, ...optional];
  const map = new Map();
  allItems.forEach((item) => {
    const key = normalizeKey(item?.item);
    if (!key) return;
    map.set(key, item?.status ?? null);
  });
  return map;
}

function resolveSeriesStatus(indicator, statusMap) {
  const key = normalizeKey(indicator);
  const candidates = [key];
  if (key.includes('혈압')) candidates.push(normalizeKey('혈압(최고/최저)'));
  if (key.includes('bmi')) candidates.push(normalizeKey('체질량지수'));
  if (key.includes('gfr')) candidates.push(normalizeKey('신사구체여과율(GFR)'));
  if (key.includes('ast')) candidates.push(normalizeKey('에이에스티(AST, SGOT)'));
  if (key.includes('alt')) candidates.push(normalizeKey('에이엘티(ALT, SGPT)'));
  if (key.includes('gtp')) candidates.push(normalizeKey('감마지티피(y-GTP)'));

  for (const candidate of candidates) {
    const found = statusMap.get(candidate);
    if (found) return found;
  }
  return { code: 'unknown', label: '확인필요' };
}

function buildValuesFromCheckupItem(item) {
  const points = [];

  if (Array.isArray(item?.history)) {
    item.history.forEach((point) => {
      const numericValue = typeof point?.numericValue === 'number'
        ? point.numericValue
        : toNumber(point?.value);
      if (numericValue === null) return;
      points.push({
        date: String(point?.date ?? ''),
        value: numericValue,
      });
    });
  }

  if (item?.latest) {
    const numericValue = typeof item.latest.numericValue === 'number'
      ? item.latest.numericValue
      : toNumber(item.latest.value);
    if (numericValue !== null) {
      points.push({
        date: String(item.latest.date ?? ''),
        value: numericValue,
      });
    }
  }

  const dedup = new Map();
  points.forEach((point) => {
    const key = `${point.date}|${point.value}`;
    if (!dedup.has(key)) dedup.set(key, point);
  });

  return Array.from(dedup.values()).sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

function buildItemMap(healthCheckupOverview) {
  const common = Array.isArray(healthCheckupOverview?.commonItems) ? healthCheckupOverview.commonItems : [];
  const optional = Array.isArray(healthCheckupOverview?.optionalItems) ? healthCheckupOverview.optionalItems : [];
  const allItems = [...common, ...optional];
  const map = new Map();
  allItems.forEach((item) => {
    const key = normalizeKey(item?.item);
    if (!key) return;
    map.set(key, item);
  });
  return map;
}

function resolveSeriesItem(indicator, itemMap) {
  const key = normalizeKey(indicator);
  const candidates = [key];
  if (key.includes('혈압')) candidates.push(normalizeKey('혈압(최고/최저)'));
  if (key.includes('bmi')) candidates.push(normalizeKey('체질량지수'));
  if (key.includes('gfr')) candidates.push(normalizeKey('신사구체여과율(GFR)'));
  if (key.includes('ast')) candidates.push(normalizeKey('에이에스티(AST, SGOT)'));
  if (key.includes('alt')) candidates.push(normalizeKey('에이엘티(ALT, SGPT)'));
  if (key.includes('gtp')) candidates.push(normalizeKey('감마지티피(y-GTP)'));

  for (const candidate of candidates) {
    const found = itemMap.get(candidate);
    if (found) return found;
  }
  return null;
}

function resolveThresholdLines(indicator, patientGender) {
  const key = normalizeKey(indicator);
  const normalizedGender = normalizeGenderValue(patientGender);

  if (key.includes('체질량지수') || key.includes('bmi')) {
    return [
      { value: 18.5, label: '저체중 기준', tone: 'neutral', operator: '미만' },
      { value: 25, label: '과체중 기준', tone: 'borderline', operator: '이상' },
      { value: 30, label: '비만 기준', tone: 'suspected', operator: '이상' },
    ];
  }
  if (key.includes('허리둘레')) {
    if (normalizedGender === 'male') {
      return [
        { value: 90, label: '복부비만 기준', tone: 'suspected', operator: '이상' },
      ];
    }
    if (normalizedGender === 'female') {
      return [
        { value: 85, label: '복부비만 기준', tone: 'suspected', operator: '이상' },
      ];
    }
    return [];
  }
  if (key.includes('혈압이완기') || key.includes('이완기혈압')) {
    return [
      { value: 80, label: '경계 기준', tone: 'borderline', operator: '이상' },
      { value: 90, label: '의심 기준', tone: 'suspected', operator: '이상' },
    ];
  }
  if (key.includes('혈압수축기') || key.includes('수축기혈압')) {
    return [
      { value: 120, label: '경계 기준', tone: 'borderline', operator: '이상' },
      { value: 140, label: '의심 기준', tone: 'suspected', operator: '이상' },
    ];
  }
  if (key.includes('공복혈당')) {
    return [
      { value: 100, label: '경계 기준', tone: 'borderline', operator: '이상' },
      { value: 126, label: '의심 기준', tone: 'suspected', operator: '이상' },
    ];
  }
  if (key.includes('총콜레스테롤')) {
    return [
      { value: 200, label: '경계 기준', tone: 'borderline', operator: '이상' },
      { value: 240, label: '의심 기준', tone: 'suspected', operator: '이상' },
    ];
  }
  if (key.includes('저밀도ldl콜레스테롤') || key.includes('ldl')) {
    return [
      { value: 130, label: '경계 기준', tone: 'borderline', operator: '이상' },
      { value: 160, label: '의심 기준', tone: 'suspected', operator: '이상' },
    ];
  }
  if (key.includes('중성지방')) {
    return [
      { value: 150, label: '경계 기준', tone: 'borderline', operator: '이상' },
      { value: 200, label: '의심 기준', tone: 'suspected', operator: '이상' },
    ];
  }
  if (key.includes('신사구체여과율') || key.includes('gfr')) {
    return [
      { value: 60, label: '의심 기준', tone: 'suspected', operator: '미만' },
    ];
  }
  if (key.includes('혈청크레아티닌')) {
    return [
      { value: 1.6, label: '의심 기준', tone: 'suspected', operator: '초과' },
    ];
  }
  if (key.includes('혈색소')) {
    if (normalizedGender === 'male') {
      return [
        { value: 13, label: '정상 하한', tone: 'neutral', operator: '이상' },
        { value: 16.5, label: '정상 상한', tone: 'neutral', operator: '이하' },
      ];
    }
    if (normalizedGender === 'female') {
      return [
        { value: 12, label: '정상 하한', tone: 'neutral', operator: '이상' },
        { value: 15.5, label: '정상 상한', tone: 'neutral', operator: '이하' },
      ];
    }
    return [
      { value: 12, label: '정상 하한', tone: 'neutral', operator: '이상' },
      { value: 16.5, label: '정상 상한', tone: 'neutral', operator: '이하' },
    ];
  }
  if (key.includes('에이에스티') || key.includes('ast')) {
    return [
      { value: 41, label: '경계 기준', tone: 'borderline', operator: '이상' },
      { value: 51, label: '의심 기준', tone: 'suspected', operator: '이상' },
    ];
  }
  if (key.includes('에이엘티') || key.includes('alt')) {
    return [
      { value: 41, label: '경계 기준', tone: 'borderline', operator: '이상' },
      { value: 51, label: '의심 기준', tone: 'suspected', operator: '이상' },
    ];
  }
  if (key.includes('감마지티피') || key.includes('gtp')) {
    if (normalizedGender === 'male') {
      return [
        { value: 64, label: '경계 기준', tone: 'borderline', operator: '이상' },
        { value: 78, label: '의심 기준', tone: 'suspected', operator: '이상' },
      ];
    }
    if (normalizedGender === 'female') {
      return [
        { value: 36, label: '경계 기준', tone: 'borderline', operator: '이상' },
        { value: 46, label: '의심 기준', tone: 'suspected', operator: '이상' },
      ];
    }
    return [
      { value: 36, label: '경계 기준', tone: 'borderline', operator: '이상' },
      { value: 78, label: '의심 기준', tone: 'suspected', operator: '이상' },
    ];
  }
  return [];
}

function thresholdStroke(tone) {
  if (tone === 'suspected') return '#cb3553';
  if (tone === 'borderline') return '#b87400';
  return '#8da2b8';
}

function thresholdLabelTone(tone) {
  if (tone === 'suspected') {
    return {
      fill: '#fff4f6',
      stroke: '#f5c9d2',
      text: '#b13a52',
    };
  }
  if (tone === 'borderline') {
    return {
      fill: '#fff8eb',
      stroke: '#f1dfb7',
      text: '#956204',
    };
  }
  return {
    fill: '#f5f8fb',
    stroke: '#dbe5ef',
    text: '#667b95',
  };
}

function formatThresholdLabel(line) {
  if (!line) return '-';
  const base = `${line.label} ${formatNumber(line.value)}`.trim();
  return line.operator ? `${base} ${line.operator}` : base;
}

function resolveMinVisualRange(indicator, averageValue, hasThresholds) {
  const key = normalizeKey(indicator);
  const avg = typeof averageValue === 'number' && Number.isFinite(averageValue)
    ? Math.abs(averageValue)
    : 0;

  if (key.includes('신장')) {
    return 4;
  }
  if (key.includes('체중')) {
    return 8;
  }
  if (key.includes('허리둘레')) {
    return 12;
  }
  if (key.includes('체질량지수') || key.includes('bmi')) {
    return 6;
  }
  if (key.includes('혈압')) {
    return 20;
  }
  if (key.includes('공복혈당')) {
    return 18;
  }
  if (key.includes('신사구체여과율') || key.includes('gfr')) {
    return 20;
  }
  if (key.includes('혈청크레아티닌')) {
    return 0.8;
  }
  if (hasThresholds) {
    return Math.max(avg * 0.08, 1);
  }
  return Math.max(avg * 0.03, 1);
}

function resolveAxisStep(indicator, hasThresholds) {
  const key = normalizeKey(indicator);

  if (key.includes('신장')) {
    return 1;
  }
  if (key.includes('혈압')) {
    return 10;
  }
  if (key.includes('체중') || key.includes('허리둘레')) {
    return 2;
  }
  if (key.includes('체질량지수') || key.includes('bmi')) {
    return 1;
  }
  if (key.includes('혈청크레아티닌')) {
    return 0.2;
  }
  if (hasThresholds) {
    return 1;
  }
  return 0.5;
}

function roundDown(value, step) {
  return Math.floor(value / step) * step;
}

function roundUp(value, step) {
  return Math.ceil(value / step) * step;
}

function resolvePointStatusCode(indicator, value, patientGender) {
  const key = normalizeKey(indicator);
  const normalizedGender = normalizeGenderValue(patientGender);

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 'unknown';
  }

  if (key.includes('체질량지수') || key.includes('bmi')) {
    if (value < 18.5) return 'borderline';
    if (value < 25) return 'normal';
    if (value < 30) return 'borderline';
    return 'suspected';
  }

  if (key.includes('허리둘레')) {
    if (normalizedGender === 'male') {
      return value >= 90 ? 'suspected' : 'normal';
    }
    if (normalizedGender === 'female') {
      return value >= 85 ? 'suspected' : 'normal';
    }
    if (value >= 90) return 'suspected';
    if (value >= 85) return 'borderline';
    return 'normal';
  }

  if (key.includes('혈압이완기') || key.includes('이완기혈압')) {
    if (value >= 90) return 'suspected';
    if (value >= 80) return 'borderline';
    return 'normal';
  }

  if (key.includes('혈압수축기') || key.includes('수축기혈압')) {
    if (value >= 140) return 'suspected';
    if (value >= 120) return 'borderline';
    return 'normal';
  }

  if (key.includes('공복혈당')) {
    if (value >= 126) return 'suspected';
    if (value >= 100) return 'borderline';
    return 'normal';
  }

  if (key.includes('총콜레스테롤')) {
    if (value >= 240) return 'suspected';
    if (value >= 200) return 'borderline';
    return 'normal';
  }

  if (key.includes('저밀도ldl콜레스테롤') || key.includes('ldl')) {
    if (value >= 160) return 'suspected';
    if (value >= 130) return 'borderline';
    return 'normal';
  }

  if (key.includes('중성지방')) {
    if (value >= 200) return 'suspected';
    if (value >= 150) return 'borderline';
    return 'normal';
  }

  if (key.includes('신사구체여과율') || key.includes('gfr')) {
    return value < 60 ? 'suspected' : 'normal';
  }

  if (key.includes('혈청크레아티닌')) {
    return value > 1.6 ? 'suspected' : 'normal';
  }

  if (key.includes('혈색소')) {
    if (normalizedGender === 'male') {
      if (value < 13 || value > 16.5) return 'suspected';
      return 'normal';
    }
    if (normalizedGender === 'female') {
      if (value < 12 || value > 15.5) return 'suspected';
      return 'normal';
    }
    if (value < 12 || value > 16.5) return 'suspected';
    return 'normal';
  }

  if (key.includes('에이에스티') || key.includes('ast')) {
    if (value >= 51) return 'suspected';
    if (value >= 41) return 'borderline';
    return 'normal';
  }

  if (key.includes('에이엘티') || key.includes('alt')) {
    if (value >= 51) return 'suspected';
    if (value >= 41) return 'borderline';
    return 'normal';
  }

  if (key.includes('감마지티피') || key.includes('gtp')) {
    if (normalizedGender === 'male') {
      if (value >= 78) return 'suspected';
      if (value >= 64) return 'borderline';
      return 'normal';
    }
    if (normalizedGender === 'female') {
      if (value >= 46) return 'suspected';
      if (value >= 36) return 'borderline';
      return 'normal';
    }
    if (value >= 78) return 'suspected';
    if (value >= 36) return 'borderline';
    return 'normal';
  }

  return 'unknown';
}

function pointToneMeta(statusCode) {
  switch (statusCode) {
    case 'suspected':
      return {
        pointFill: '#cb3553',
        pointStroke: '#ffffff',
        labelFill: '#fff1f4',
        labelStroke: '#f6c4d1',
        labelText: '#b12f48',
      };
    case 'borderline':
      return {
        pointFill: '#c88416',
        pointStroke: '#ffffff',
        labelFill: '#fff7e8',
        labelStroke: '#f4ddb0',
        labelText: '#966100',
      };
    case 'normal':
      return {
        pointFill: '#1f9b72',
        pointStroke: '#ffffff',
        labelFill: '#eef9f4',
        labelStroke: '#c7ecd9',
        labelText: '#167556',
      };
    default:
      return {
        pointFill: '#6f839a',
        pointStroke: '#ffffff',
        labelFill: '#f4f7fa',
        labelStroke: '#dbe4ee',
        labelText: '#60748d',
      };
  }
}

function TrendMainChart({ values, thresholds, indicator, patientGender }) {
  if (!values.length) {
    return <div className="nhis-trend-empty">측정값 없음</div>;
  }

  const trendLineStroke = '#4e6684';
  const width = 380;
  const height = 200;
  const pad = { left: 40, right: 16, top: 16, bottom: 40 };
  const chartW = width - pad.left - pad.right;
  const chartH = height - pad.top - pad.bottom;
  const bottomY = pad.top + chartH;

  const numericValues = values.map((point) => point.value);
  const thresholdValues = (thresholds ?? []).map((line) => line.value).filter((value) => typeof value === 'number');
  const allValues = [...numericValues, ...thresholdValues];
  const rawMin = Math.min(...allValues);
  const rawMax = Math.max(...allValues);
  const spread = rawMax - rawMin;
  const averageValue = numericValues.reduce((sum, value) => sum + value, 0) / Math.max(numericValues.length, 1);
  const minVisualRange = resolveMinVisualRange(indicator, averageValue, thresholdValues.length > 0);
  const targetSpread = Math.max(spread, minVisualRange);
  const center = (rawMin + rawMax) / 2;
  const step = resolveAxisStep(indicator, thresholdValues.length > 0);
  const padding = targetSpread * 0.08;
  const min = roundDown(center - targetSpread / 2 - padding, step);
  const max = roundUp(center + targetSpread / 2 + padding, step);
  const range = max - min || 1;

  const points = values.map((point, idx) => {
    const x = pad.left + (idx / Math.max(values.length - 1, 1)) * chartW;
    const y = pad.top + (1 - ((point.value - min) / range)) * chartH;
    const statusCode = resolvePointStatusCode(indicator, point.value, patientGender);
    return { x, y, statusCode, ...point };
  });

  const line = points.map((p) => `${p.x},${p.y}`).join(' ');
  const gridRows = [0, 0.5, 1].map((ratio) => ({
    y: pad.top + ratio * chartH,
    label: formatNumber(max - ratio * range),
  }));

  if (points.length === 1) {
    const p = points[0];
    const tone = pointToneMeta(p.statusCode);
    const labelText = formatNumber(p.value);
    const labelWidth = Math.min(78, 20 + labelText.length * 7);
    const labelHeight = 22;
    const labelX = Math.max(pad.left, Math.min(width - pad.right - labelWidth, p.x - labelWidth / 2));
    const labelY = Math.max(4, p.y - 30);
    const labelTextY = labelY + 14;
    return (
      <svg viewBox={`0 0 ${width} ${height}`} className="nhis-trend-main-chart" role="img" aria-label="건강 지표 추이">
        {(thresholds ?? []).map((line) => {
          const y = pad.top + (1 - ((line.value - min) / range)) * chartH;
          const labelText = formatThresholdLabel(line);
          const labelTone = thresholdLabelTone(line.tone);
          const labelWidth = Math.min(110, 20 + labelText.length * 5.2);
          const labelHeight = 18;
          const labelX = Math.max(pad.left + 12, Math.min(width - pad.right - labelWidth, pad.left + 34));
          const labelY = line.tone === 'borderline'
            ? Math.min(bottomY - labelHeight - 2, y + 5)
            : Math.max(4, y - labelHeight - 4);
          return (
            <g key={`threshold-single-${line.label}-${line.value}`}>
              <line
                x1={pad.left}
                y1={y}
                x2={width - pad.right}
                y2={y}
                stroke={thresholdStroke(line.tone)}
                strokeWidth="1.2"
                strokeDasharray="4 4"
              />
              <rect
                x={labelX}
                y={labelY}
                width={labelWidth}
                height={labelHeight}
                rx="9"
                fill={labelTone.fill}
                stroke={labelTone.stroke}
                strokeWidth="1"
              />
              <text
                x={labelX + labelWidth / 2}
                y={labelY + 12}
                textAnchor="middle"
                className="nhis-trend-threshold-label"
                fill={labelTone.text}
              >
                {labelText}
              </text>
            </g>
          );
        })}
        <line x1={pad.left} y1={bottomY} x2={width - pad.right} y2={bottomY} stroke="#e7eef6" strokeWidth="1" />
        <circle cx={p.x} cy={p.y} r="5" fill={tone.pointFill} stroke={tone.pointStroke} strokeWidth="1.4" />
        <rect x={labelX} y={labelY} width={labelWidth} height={labelHeight} rx="11" fill={tone.labelFill} stroke={tone.labelStroke} strokeWidth="1" />
        <text x={labelX + labelWidth / 2} y={labelTextY} textAnchor="middle" className="nhis-trend-point-label" fill={tone.labelText}>
          {labelText}
        </text>
        <text x={p.x} y={bottomY + 20} textAnchor="middle" className="nhis-trend-axis-label">{fmtAxisDate(p.date)}</text>
      </svg>
    );
  }

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="nhis-trend-main-chart" role="img" aria-label="건강 지표 추이">
      {gridRows.map((row, idx) => (
        <g key={`grid-${idx}`}>
          <line x1={pad.left} y1={row.y} x2={width - pad.right} y2={row.y} stroke={idx === 2 ? '#e4ebf3' : '#f2f6fb'} strokeWidth="1" />
          <text x={8} y={row.y + 3} className="nhis-trend-grid-label">{row.label}</text>
        </g>
      ))}

      {(thresholds ?? []).map((line) => {
        const y = pad.top + (1 - ((line.value - min) / range)) * chartH;
        const labelText = formatThresholdLabel(line);
        const labelTone = thresholdLabelTone(line.tone);
        const labelWidth = Math.min(110, 20 + labelText.length * 5.2);
        const labelHeight = 18;
        const labelX = Math.max(pad.left + 12, Math.min(width - pad.right - labelWidth, pad.left + 34));
        const labelY = line.tone === 'borderline'
          ? Math.min(bottomY - labelHeight - 2, y + 5)
          : Math.max(4, y - labelHeight - 4);
        return (
          <g key={`threshold-${line.label}-${line.value}`}>
            <line
              x1={pad.left}
              y1={y}
              x2={width - pad.right}
              y2={y}
              stroke={thresholdStroke(line.tone)}
              strokeWidth="1.2"
              strokeDasharray="4 4"
            />
            <rect
              x={labelX}
              y={labelY}
              width={labelWidth}
              height={labelHeight}
              rx="9"
              fill={labelTone.fill}
              stroke={labelTone.stroke}
              strokeWidth="1"
            />
            <text
              x={labelX + labelWidth / 2}
              y={labelY + 12}
              textAnchor="middle"
              className="nhis-trend-threshold-label"
              fill={labelTone.text}
            >
              {labelText}
            </text>
          </g>
        );
      })}
      <polyline points={line} fill="none" stroke={trendLineStroke} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />

      {points.map((point, idx) => (
        <g key={`point-${idx}`}>
          {(() => {
            const isLatest = idx === points.length - 1;
            const tone = pointToneMeta(point.statusCode);
            const labelText = formatNumber(point.value);
            const labelWidth = Math.min(isLatest ? 78 : 66, 18 + labelText.length * 7);
            const labelHeight = isLatest ? 22 : 20;
            const labelX = Math.max(pad.left, Math.min(width - pad.right - labelWidth, point.x - labelWidth / 2));
            const labelY = Math.max(4, point.y - (isLatest ? 30 : 26));
            const labelTextY = labelY + (isLatest ? 14 : 13);

            return (
              <>
                <rect
                  x={labelX}
                  y={labelY}
                  width={labelWidth}
                  height={labelHeight}
                  rx="11"
                  fill={tone.labelFill}
                  stroke={tone.labelStroke}
                  strokeWidth={isLatest ? '1.1' : '1'}
                />
                <text
                  x={labelX + labelWidth / 2}
                  y={labelTextY}
                  textAnchor="middle"
                  className={`nhis-trend-point-label${isLatest ? ' is-latest' : ''}`}
                  fill={tone.labelText}
                >
                  {labelText}
                </text>
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={isLatest ? 5.2 : 3.8}
                  fill={tone.pointFill}
                  stroke={tone.pointStroke}
                  strokeWidth={isLatest ? '1.8' : '1.2'}
                />
              </>
            );
          })()}
          <text x={point.x} y={bottomY + 20} textAnchor="middle" className="nhis-trend-axis-label">{fmtAxisDate(point.date)}</text>
        </g>
      ))}
    </svg>
  );
}

export default function NhisTrendChart({ trendData, healthCheckupOverview, patientGender }) {
  const [activeDomain, setActiveDomain] = useState('');
  const [activeMetric, setActiveMetric] = useState('');
  const [domainScrollState, setDomainScrollState] = useState({ left: false, right: false });
  const [metricScrollState, setMetricScrollState] = useState({ left: false, right: false });
  const domainTabsRef = useRef(null);
  const metricTabsRef = useRef(null);

  const preparedSeries = useMemo(() => {
    const statusMap = buildStatusMap(healthCheckupOverview);
    const itemMap = buildItemMap(healthCheckupOverview);
    const mergedSeries = new Map();

    (Array.isArray(trendData) ? trendData : []).forEach((series) => {
      const indicator = series?.indicator || '-';
      const item = resolveSeriesItem(indicator, itemMap);
      const prepared = {
        key: normalizeKey(indicator),
        rawIndicator: indicator,
        indicator: formatIndicatorLabel(indicator),
        unit: series?.unit || item?.unit || '',
        domain: classifyDomainByIndicator(indicator),
        values: normalizeValues(series?.values),
        status: resolveSeriesStatus(indicator, statusMap),
        latestDate: item?.latest?.date || null,
        thresholds: resolveThresholdLines(indicator, patientGender),
      };
      mergedSeries.set(prepared.key, prepared);
    });

    const common = Array.isArray(healthCheckupOverview?.commonItems) ? healthCheckupOverview.commonItems : [];
    const optional = Array.isArray(healthCheckupOverview?.optionalItems) ? healthCheckupOverview.optionalItems : [];
    [...common, ...optional].forEach((item) => {
      if (normalizeKey(item?.item) === normalizeKey('혈압(최고/최저)')) {
        return;
      }

      const values = buildValuesFromCheckupItem(item);
      if (!values.length) return;

      const indicator = formatIndicatorLabel(item?.item);
      const key = normalizeKey(indicator);
      if (mergedSeries.has(key)) return;

      mergedSeries.set(key, {
        key,
        rawIndicator: item?.item || indicator,
        indicator,
        unit: item?.unit || '',
        domain: classifyDomainByIndicator(item?.item),
        values,
        status: item?.status || resolveSeriesStatus(item?.item, statusMap),
        latestDate: item?.latest?.date || (values.length ? values[values.length - 1].date : null),
        thresholds: resolveThresholdLines(item?.item, patientGender),
      });
    });

    return Array.from(mergedSeries.values())
      .filter((series) => series.values.length > 0)
      .sort((a, b) => {
        const ap = STATUS_PRIORITY[a.status?.code] ?? 0;
        const bp = STATUS_PRIORITY[b.status?.code] ?? 0;
        if (ap !== bp) return bp - ap;
        return a.indicator.localeCompare(b.indicator, 'ko');
      });
  }, [healthCheckupOverview, patientGender, trendData]);

  const domainMap = useMemo(() => {
    const map = new Map();
    DOMAIN_ORDER.forEach((domain) => map.set(domain, []));
    preparedSeries.forEach((series) => {
      if (!map.has(series.domain)) map.set(series.domain, []);
      map.get(series.domain).push(series);
    });
    return map;
  }, [preparedSeries]);

  const availableDomains = useMemo(
    () => DOMAIN_ORDER.filter((domain) => (domainMap.get(domain) ?? []).length > 0),
    [domainMap],
  );

  useEffect(() => {
    if (!availableDomains.length) {
      setActiveDomain('');
      return;
    }
    if (!availableDomains.includes(activeDomain)) {
      setActiveDomain(availableDomains[0]);
    }
  }, [activeDomain, availableDomains]);

  const activeDomainSeries = useMemo(
    () => (domainMap.get(activeDomain) ?? []),
    [domainMap, activeDomain],
  );

  useEffect(() => {
    if (!activeDomainSeries.length) {
      setActiveMetric('');
      return;
    }
    if (!activeDomainSeries.some((series) => series.key === activeMetric)) {
      setActiveMetric(activeDomainSeries[0].key);
    }
  }, [activeDomainSeries, activeMetric]);

  useEffect(() => {
    const syncScrollState = (node, setter) => {
      if (!node) {
        setter({ left: false, right: false });
        return () => {};
      }

      const update = () => {
        const maxScrollLeft = node.scrollWidth - node.clientWidth;
        if (maxScrollLeft <= 1) {
          setter({ left: false, right: false });
          return;
        }
        setter({
          left: node.scrollLeft > 2,
          right: node.scrollLeft < maxScrollLeft - 2,
        });
      };

      update();
      node.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);

      return () => {
        node.removeEventListener('scroll', update);
        window.removeEventListener('resize', update);
      };
    };

    const cleanupDomain = syncScrollState(domainTabsRef.current, setDomainScrollState);
    const cleanupMetric = syncScrollState(metricTabsRef.current, setMetricScrollState);

    return () => {
      cleanupDomain();
      cleanupMetric();
    };
  }, [availableDomains, activeDomainSeries, activeDomain, activeMetric]);

  if (!preparedSeries.length) return null;

  const activeSeries = activeDomainSeries.find((series) => series.key === activeMetric) ?? activeDomainSeries[0];

  return (
    <section className="nhis-trend-card">
      <div className="nhis-trend-header">
        <h3>건강 지표 추이</h3>
        <p>최근 검진 이력에서 지표별 변화를 확인합니다.</p>
      </div>

      <div className="nhis-trend-tab-group">
        <span className="nhis-trend-tab-group-label">질환군</span>
        <div
          className={`nhis-trend-scroll-shell${domainScrollState.left ? ' has-left-fade' : ''}${domainScrollState.right ? ' has-right-fade' : ''}`}
        >
          <div
            ref={domainTabsRef}
            className="nhis-trend-domain-tabs"
            role="tablist"
            aria-label="질환군 선택"
          >
            {availableDomains.map((domain) => (
              <button
                key={domain}
                type="button"
                className={`nhis-trend-domain-tab${activeDomain === domain ? ' is-active' : ''}`}
                onClick={() => setActiveDomain(domain)}
                aria-pressed={activeDomain === domain}
              >
                <span className="nhis-trend-domain-name">{domain}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="nhis-trend-tab-group nhis-trend-tab-group-secondary">
        <span className="nhis-trend-tab-group-label">세부 지표</span>
        <div
          className={`nhis-trend-scroll-shell${metricScrollState.left ? ' has-left-fade' : ''}${metricScrollState.right ? ' has-right-fade' : ''}`}
        >
          <div
            ref={metricTabsRef}
            className="nhis-trend-metric-tabs"
            role="tablist"
            aria-label="세부 지표 선택"
          >
            {activeDomainSeries.map((series) => (
              <button
                key={series.key}
                type="button"
                className={`nhis-trend-metric-tab${series.key === activeMetric ? ' is-active' : ''}`}
                onClick={() => setActiveMetric(series.key)}
                aria-pressed={series.key === activeMetric}
              >
                {series.indicator}
              </button>
            ))}
          </div>
        </div>
      </div>

      {activeSeries && (
        <>
          <div className="nhis-trend-selected-head">
            <div className="nhis-trend-selected-title">
              <p>
                {activeSeries.latestDate ? `최근 검진 ${fmtDate(activeSeries.latestDate)}` : '최근 검진 이력'}
                {activeSeries.unit ? ` · 단위 ${activeSeries.unit}` : ''}
              </p>
            </div>
          </div>

          <div className="nhis-trend-main-wrap">
            <TrendMainChart
              values={activeSeries.values}
              thresholds={activeSeries.thresholds}
              indicator={activeSeries.rawIndicator || activeSeries.indicator}
              patientGender={patientGender}
            />
          </div>

          <p className="nhis-trend-threshold-note">
            {activeSeries.thresholds.length > 0
              ? '점선은 상태 판단 기준선입니다.'
              : '이 지표는 기준선 없이 추이만 제공합니다.'}
          </p>
        </>
      )}
    </section>
  );
}
